import React, { useState, useEffect } from 'react';

import {
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';

import Autocomplete from '@mui/material/Autocomplete';

import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';


function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };


export default function GoogleMaps(props) {
  const [inputValue, setInputValue] = useState();
  const [value, setValue] = useState(props.initialValue);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loaded = React.useRef(false);

  if (typeof window !== 'undefined' && !loaded.current) {
    if (!document.querySelector('#google-maps')) {
      loadScript(
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyDoOHc6XRVaQso0d8EybI4-xaAv965tCXA&libraries=places',
        document.querySelector('head'),
        'google-maps',
      );
      // use secrets to replace key
    }

    loaded.current = true;
  }

  const handleChange = (event) => {
    setInputValue(event.target.value);
  };

  const fetch = React.useMemo(
    () =>
      throttle((request, callback) => {
        setLoading(true);
        autocompleteService.current.getPlacePredictions(
          {
            ...request,
            componentRestrictions: {country: 'za'},
            types: props.types,
          },
          callback
        );
      }, 200),
    [props.types],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '' || inputValue === undefined) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        setLoading(false);
        setOptions(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetch]);

  return (
    <Autocomplete
      id="google-map-autocomplete"
      value={value}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      isOptionEqualToValue={(option, value) => option.structured_formatting.main_text === value}
      filterOptions={(x) => x}
      options={options}
      loading={loading}
      autoComplete
      loadingText={'Searching...'}
      noOptionsText={'Type to search'}
      autoFocus={props.autoFocus}
      includeInputInList
      sx={{
        '&.MuiAutocomplete-listbox': {
          backgroundImage: "url('/powered_by_google_on_white.png')",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right bottom'
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={props.placeholder || "Near"}
          autoComplete="off"
          variant="outlined"
          fullWidth
          onChange={handleChange}
          InputProps={{
            autoComplete: "off",
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <IconButton edge="start" aria-label="Seacrh location" size="large">
                    <LocationCityIcon />
                  </IconButton>
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const matches = option.structured_formatting.main_text_matched_substrings;
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );

        return (
          <Grid container alignItems="center" {...props}>
            <Grid item>
              <LocationOnIcon sx={{
                color: 'text.secondary',
                mr: 2,
              }} />
            </Grid>
            <Grid item xs>
              {parts.map((part, index) => (
                <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                  {part.text}
                </span>
              ))}

              <Typography variant="body2" color="textSecondary">
                {option.structured_formatting.secondary_text}
              </Typography>
            </Grid>
          </Grid>
        );
      }}
      onChange={(event, value) => {
        const mainText = value ? value.description : '';
        setValue(mainText);
        if (props.onInputChange) {
          props.onInputChange(mainText);
        } else if (props.setFieldValue) {
          props.setFieldValue('address', mainText);
        }
      }}
    />
  );
}
